import { createSlice } from "@reduxjs/toolkit";
import { filter, mapTo } from "rxjs/operators";

import { getThree, getOptions, getDecorators, autostart } from "../../data";

const initialState = {
  started: !!autostart || process.env.NODE_ENV === "development",
  loaded: false,
  loading: false,
  loadingProgress: 0,
  ...getThree(),
};

const options = getOptions();
const decorators = getDecorators();

export const threeSlice = createSlice({
  name: "three",
  initialState,
  reducers: {
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setLoaded: (state, { payload }) => {
      state.loaded = payload;
    },
    setLoadingProgress: (state, { payload }) => {
      state.loadingProgress = payload;
    },
    addEntity: (state, action) => {
      state.entities.push(action.payload);
    },
    removeEntity: (state, { payload: { id } }) => {
      const index = state.entities.findIndex((item) => item.id === id);
      state.entities.splice(index, 1);
    },
    updateEntity: (state, { payload }) => {
      const index = state.entities.findIndex((item) => item.id === payload.id);
      state.entities[index] = { ...state.entities[index], ...payload };
    },
    updateEntityVisibilities: (state, { payload = {} }) => {
      state.entities = state.entities.map((entity) => {
        if (payload.hasOwnProperty(entity.id)) {
          entity.visible = payload[entity.id];
        }
        return entity;
      });
    },
    updateAnimationClip: (state, action) => {
      const { entityId, clipId, patch } = action.payload;

      if (!entityId || !clipId || !patch) {
        return;
      }

      const entityIndex = state.entities.findIndex(
        (item) => item.id === entityId
      );

      if (entityIndex === -1) {
        return;
      }

      const entity = state.entities[entityIndex];

      if (!entity || !entity.animation || !entity.animation.clips) {
        return;
      }

      const clipIndex = entity.animation.clips.findIndex(
        (item) => item.id === clipId
      );

      if (clipIndex === -1) {
        return;
      }

      entity.animation.clips[clipIndex] = {
        ...entity.animation.clips[clipIndex],
        ...patch,
      };
    },

    addModel: (state, action) => {
      state.models.push(action.payload);
    },
    addMaterial: (state, action) => {
      state.materials.push(action.payload);
    },
    updateMaterial: (state, { payload }) => {
      const index = state.materials.findIndex((item) => item.id === payload.id);
      if (index !== -1) {
        state.materials[index] = { ...state.materials[index], ...payload };
      }
    },
    addTexture: (state, action) => {
      state.textures.push(action.payload);
    },
    setBackgroundColor: (state, action) => {
      state.entities.forEach((entity) => {
        if (entity.scene) {
          entity.scene.background = action.payload;
        }
      });
    },

    updateVisibilitiesByTags: (state, action) => {
      state.entities.forEach((entity) => {
        if (entity.hasOwnProperty("tags")) {
          const res = entity.tags.filter(
            (tag) => !action.payload.includes(tag)
          );
          entity.visible = res.length === 0;
        }
      });
    },

    setCamera: (state, { payload }) => {
      // const cameraEntity = state.entities
      state.camera = payload;
    },

    setHover: (state, { payload }) => {
      const target =
        (payload &&
          payload.entity &&
          payload.entity.interaction &&
          (payload.entity.interaction.target || payload.entity.id)) ||
        null;

      state.hover = target;
    },
    rotate: (state, { payload }) => {
      if (!state.hasOwnProperty("rotateCount")) {
        state.rotateCount = 0;
      }
      state.rotateCount++;
    },
    start: (state, { payload }) => {
      state.started = true;
    },
  },
});

export const {
  addEntity,
  removeEntity,
  updateEntity,
  updateEntityVisibilities,
  updateAnimationClip,
  addModel,
  addMaterial,
  updateMaterial,
  addTexture,
  setBackgroundColor,
  setCamera,
  updateVisibilitiesByTags,
  setHover,
  rotate,
  start,
  setLoading,
  setLoaded,
} = threeSlice.actions;

export const selectEntities = (state) => state.three.entities;
export const selectModels = (state) => state.three.models;
export const selectMaterials = (state) => state.three.materials;
export const selectTextures = (state) => state.three.textures;
export const selectStarted = (state) => state.three.started;
export const selectLoaded = (state) => state.three.loaded;
export const selectLoading = (state) => state.three.loading;
export const selectLoadingProgress = (state) => state.three.loadingProgress;

const createFilter = ({ tractor }) => {
  const filters = [];

  if (tractor.state.type) filters.push(`${tractor.state.type}_Basemodel`);
  if (tractor.state.loader) filters.push(`${tractor.state.type}_Loader`);
  if (tractor.state.premiumInterior)
    filters.push(`${tractor.state.type}_PremiumComfort`);
  if (tractor.state.technologyPackage)
    filters.push(`${tractor.state.type}_Technology`);
  if (tractor.state.twintrack) filters.push(`${tractor.state.type}_Twintrack`);
  if (tractor.state.coolbox) filters.push(`${tractor.state.type}_Coolbox`);
  if (tractor.state.skyview) filters.push(`${tractor.state.type}_Skyview`);
  if (tractor.state.forestTank)
    filters.push(`${tractor.state.type}_Foresttank`);
  if (tractor.state.flareExtensions)
    filters.push(`${tractor.state.type}_FlareExtensions`);
  if (tractor.state.frontLinkage)
    filters.push(`${tractor.state.type}_Frontlinkage`);
  if (tractor.state.twoDoor) filters.push(`${tractor.state.type}_Twodoor`);
  if (tractor.state.evolutionSeat)
    filters.push(`${tractor.state.type}_Evolutionseat`);
  if (tractor.state.basicControl)
    filters.push(`${tractor.state.type}_Basic_Control`);

  return ({ id }) => {
    for (const filter of filters) {
      if (id.toUpperCase().includes(filter.toUpperCase())) {
        return true;
      }
    }
    return false;
  };
};

const createCompareObject = (ids) => {
  const o = {};

  for (const option of options) {
    // if option is included
    // set visibilities according to the
    // definition
    if (ids.includes(option.id)) {
      for (const group of option.show) {
        o[group] = true;
      }
      for (const group of option.hide) {
        o[group] = false;
      }
    } else {
      // otherwise just set false for
      // undefined groups
      for (const group of option.show) {
        if (!o.hasOwnProperty(group)) {
          o[group] = false;
        }
      }
      for (const group of option.hide) {
        if (!o.hasOwnProperty(group)) {
          o[group] = false;
        }
      }
    }
  }

  return o;
};

export const threeMiddleware = (store) => (next) => (action) => {
  next(action);

  if (
    action.type === "context/setValue" &&
    action.payload?.path === "tractor/view"
  ) {
    const view = store
      .getState()
      .context?.tractor?.property?.view?.options.find(
        (option) => option.value === action.payload?.value
      );

    if (view?.camera) {
      next(
        updateEntity({
          id: "camera",
          camera: {
            ...view.camera,
          },
        })
      );
    }
  }

  if (action.type === "app/init") {
    // set default camera view
    const view = store
      .getState()
      .context?.tractor?.property?.view?.options.find(
        (option) =>
          option.value === store.getState().context?.tractor?.state?.view
      );

    if (view?.camera) {
      next(
        updateEntity({
          id: "camera",
          camera: {
            ...view.camera,
          },
        })
      );
    }
  }

  switch (action.type) {
    case "context/setValue":
    case "context/toggleValue":
    case "app/init":
      // Optimization: Ignore some setValues, eg. loaderPosition
      if (action.payload?.path === "tractor/loaderPosition") {
        break;
      }

      const filteredOptions = options
        .filter(createFilter(store.getState().context))
        .map((option) => option.id);

      let compareObject = createCompareObject(filteredOptions);

      if (typeof decorators.decorateCompareObject === "function") {
        decorators.decorateCompareObject(compareObject, store.getState());
      }

      next(updateEntityVisibilities(compareObject));

      break;

    default:
      break;
  }
};

export default threeSlice.reducer;

export const pingEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === "PING"),
    mapTo({ type: "PONG" })
  );
